import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { Priority, PriorityStatus } from "@/types";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { getPriorityStatusMeta, PRIORITY_STATUS_LABEL } from "@/components/priorities/priorityCardTokens";
import { cn } from "@/lib/utils";

/** Committed deliverables are grouped in this order; `done` collapses separately below. */
const COMMITTED_STATUS_ORDER: PriorityStatus[] = ["focus", "queue", "blocked", "waiting", "on-hold"];

const driName = (p: Priority): string =>
  p.dri?.fullName ?? p.assignments?.find((a) => a.isDri)?.assignedUser.fullName ?? "—";

function DeliverableRow({ priority, badge }: { priority: Priority; badge?: ReactNode }) {
  const meta = getPriorityStatusMeta(priority.status);
  return (
    <li>
      <Link to={`/priorities/${priority.pid}`} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/40">
        <span className="shrink-0 font-mono text-xs text-muted-foreground">{priority.code}</span>
        <span className="min-w-0 flex-1 truncate">{priority.name}</span>
        {badge}
        <span className="shrink-0 text-xs text-muted-foreground">{driName(priority)}</span>
        <Badge variant="outline" className={cn("shrink-0 gap-1", meta.className)}>
          <meta.icon className="h-3 w-3" />
          {meta.label}
        </Badge>
      </Link>
    </li>
  );
}

const CANDIDATE_BADGE = (
  <Badge variant="outline" className="shrink-0 border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
    Candidate
  </Badge>
);

interface PppDetailDeliverablesProps {
  deliverables: Priority[];
  deliverablesLoading: boolean;
  deliverablesError: boolean;
  candidates: Priority[];
  candidatesLoading: boolean;
  candidatesError: boolean;
}

export function PppDetailDeliverables({
  deliverables, deliverablesLoading, deliverablesError,
  candidates, candidatesLoading, candidatesError,
}: PppDetailDeliverablesProps) {
  const [doneOpen, setDoneOpen] = useState(false);
  const doneItems = deliverables.filter((p) => p.status === "done");

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Deliverables</h2>
        {deliverablesLoading ? (
          <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-9 w-full rounded-lg" />)}</div>
        ) : deliverablesError ? (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center text-sm text-destructive">Failed to load deliverables.</div>
        ) : deliverables.length === 0 ? (
          <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">No committed deliverables linked to this PPP yet.</div>
        ) : (
          <div className="flex flex-col gap-4">
            {COMMITTED_STATUS_ORDER.map((status) => {
              const items = deliverables.filter((p) => p.status === status);
              if (items.length === 0) return null;
              return (
                <div key={status}>
                  <p className="mb-1 text-xs font-medium text-muted-foreground">{PRIORITY_STATUS_LABEL[status]}</p>
                  <ul className="divide-y divide-border/40 rounded-lg border border-border/60">
                    {items.map((p) => <DeliverableRow key={p.pid} priority={p} />)}
                  </ul>
                </div>
              );
            })}

            {doneItems.length > 0 && (
              <Collapsible open={doneOpen} onOpenChange={setDoneOpen}>
                <CollapsibleTrigger className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
                  <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", doneOpen && "rotate-180")} />
                  Done ({doneItems.length})
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <ul className="mt-1 divide-y divide-border/40 rounded-lg border border-border/60">
                    {doneItems.map((p) => <DeliverableRow key={p.pid} priority={p} />)}
                  </ul>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Candidates</h2>
        {candidatesLoading ? (
          <div className="space-y-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-9 w-full rounded-lg" />)}</div>
        ) : candidatesError ? (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center text-sm text-destructive">Failed to load candidates.</div>
        ) : candidates.length === 0 ? (
          <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">No candidates waiting on this PPP.</div>
        ) : (
          <ul className="divide-y divide-border/40 rounded-lg border border-border/60">
            {candidates.map((p) => <DeliverableRow key={p.pid} priority={p} badge={CANDIDATE_BADGE} />)}
          </ul>
        )}
      </section>
    </div>
  );
}
